import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "./Authprovider";
import { Loader } from "@b2b/ui-components";
import AppLayout from "@b2b/ui-components/components/Layouts/AppLayout";
import {
  canAccessAdminPath,
  getAdminNavigationSections,
  getFirstGrantedAdminPath,
} from "@b2b/ui-components";
import { useChatUnreadHeader } from "../chat/ChatUnreadHeaderProvider";
import { useNotificationHeader } from "../notifications/NotificationHeaderProvider";
import { BizcoverIndiaFigmaHeader } from "../components/marketing/BizcoverIndiaFigmaHeader";
import { BizcoverIndiaFigmaFooter } from "../components/marketing/BizcoverIndiaFigmaFooter";

interface PrivateLayoutProps {
  role: "buyer" | "seller" | "admin" | "both";
}

const PrivateLayout: React.FC<PrivateLayoutProps> = ({ role }) => {
  const {
    isAuthenticated,
    isLoading,
    isSessionProfileLoading,
    userStatus,
    userRole,
    sessionEmail,
    logout,
    allowedModules,
  } = useAuth();
  const location = useLocation();
  const chatUnread = useChatUnreadHeader();
  const notifications = useNotificationHeader();
  const isProfileRoute = /\/(profile|profile_settings|userprofile)(\/|$)/.test(location.pathname);
  const isDashboardShellPath =
    location.pathname.startsWith("/buyer") ||
    location.pathname.startsWith("/seller") ||
    location.pathname.startsWith("/admin");

  if (isLoading || (isAuthenticated && isSessionProfileLoading)) {
    return <Loader size={40} color="#f97316" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (userStatus === "not_registered" || userStatus === "pending_registration") {
    return (
      <Navigate
        to="/login?tab=signup"
        replace
        state={{
          fromOAuth: true,
          prefilledEmail: sessionEmail,
        }}
      />
    );
  }

  // Role-based access control
  if (userRole && userRole !== role && role !== "both") {
    // Super Admin / Company Admin can access any private layout. Staff cannot.
    if (userRole === "admin" && allowedModules === undefined) {
      // allow
    } else {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  if (
    userRole === "admin" &&
    role === "admin" &&
    allowedModules !== undefined &&
    !canAccessAdminPath(location.pathname, allowedModules)
  ) {
    return (
      <Navigate to={getFirstGrantedAdminPath(allowedModules)} replace />
    );
  }

  return (
    <AppLayout
      role={role}
      userRole={userRole}
      onLogout={logout}
      chatUnread={chatUnread}
      notifications={notifications}
      notificationUnreadCount={notifications?.unreadCount ?? 0}
      hideShellHeader={isProfileRoute}
      hideShellFooter={isDashboardShellPath}
      customShellHeader={<BizcoverIndiaFigmaHeader />}
      customShellFooter={<BizcoverIndiaFigmaFooter />}
      adminNavigationItems={
        role === "admin"
          ? getAdminNavigationSections(allowedModules)
          : undefined
      }
    >
      <Outlet />
    </AppLayout>
  );
};

export default PrivateLayout;
